import { useMemo } from "react"
import { createBrowserHistory } from "app-base/web-shell/history"
import {
  RouteParamType,
  RouteParams as BaseRouteParams,
  createRouteNavigator,
  matchRoute,
  routePathSegment,
} from "app-base/web-shell/routing"

import { logger } from "./Logger"

export { RouteParamType }

export interface AppRoute {
  route: string[];
}

const appHistory = createBrowserHistory({
  basePath: "/app",
  onNavigate: ({ url }) => logger.debug("navigate", url),
});

export function SubscribeRoute(listener: (path: string) => void): () => void {
  return appHistory.subscribe((path) => listener(path ? `/app/${path}` : "/app/"));
}

export function GetRoute(customRoot?: string): AppRoute {
  const root = customRoot ?? appHistory.read();
  return { route: root.length === 0 ? [] : root.split("/") };
}

export function SetRoute(path: string, replace?: boolean): string {
  return appHistory.navigate(path, { replace });
}

export type RouteParamValue = number | string | string[];

export interface RouteParams {
  [key: string]: RouteParamValue;
}

export class RouterRouteParams {
  params: RouteParams;

  constructor(params: RouteParams) {
    this.params = params;
  }

  requireInt(key: string): number {
    return new BaseRouteParams(this.params).requireInt(key);
  }

  requireString(key: string): string {
    return new BaseRouteParams(this.params).requireString(key);
  }

  requireRoute(key: string): string[] {
    return [...new BaseRouteParams(this.params).requireRoute(key)];
  }
}

export type RouteHandler<T> = (params: RouterRouteParams) => T | undefined;

export interface RouteDefinition {
  navigate?: string[];
  path: string[];
  params?: { [name: string]: RouteParamType };
}

export interface RouteDefinitions {
  [name: string]: RouteDefinition;
}

export type RouterNavigateFunction = (name: string, params?: RouteParams, replace?: boolean) => boolean;

export class Router<T> {
  definitions: RouteDefinitions;
  route: AppRoute;
  routeName?: string;
  routeParams: RouteParams;
  result?: T;

  constructor(route: AppRoute, definitions: RouteDefinitions, routes: (router: Router<T>) => void) {
    this.definitions = definitions;
    this.route = route;
    this.routeParams = {};
    routes(this);
    if (this.result == undefined) {
      throw new Error(`No route matched: ${this.route.route.join("/")}, default route handler is missing.`);
    }
  }

  on(name: string, handler: RouteHandler<T>) {
    const definition = this.definitions[name];
    if (this.result != undefined || definition == undefined) {
      return;
    }
    const params = matchRoute(this.route.route, definition);
    if (params) {
      const mutableParams = Object.fromEntries(
        Object.entries(params).map(([key, value]) => [key, Array.isArray(value) ? [...value] : value]),
      ) as RouteParams;
      this.result = handler(new RouterRouteParams(mutableParams));
      this.routeName = name;
      this.routeParams = mutableParams;
    }
  }

  match(definition: RouteDefinition): RouteParams | undefined {
    const params = matchRoute(this.route.route, definition);
    return params
      ? Object.fromEntries(
        Object.entries(params).map(([key, value]) => [key, Array.isArray(value) ? [...value] : value]),
      ) as RouteParams
      : undefined;
  }

  matchSegment(definition: RouteDefinition, n: number): RouteParams | undefined {
    return matchRoute(this.route.route.slice(n), {
      ...definition,
      path: definition.path.slice(n),
    }) as RouteParams | undefined;
  }

  static navigate(definitions: RouteDefinitions, updateRoute: (path: string, replace?: boolean) => void, prefix?: string, _replace?: boolean): RouterNavigateFunction {
    const navigate = createRouteNavigator(definitions, updateRoute, { prefix });
    return (name, params, replace) => navigate(name, params, replace);
  }

  static pathSegment(segment: string, params?: RouteParams): string {
    return routePathSegment(segment, params);
  }
}

export function useRouter<T>(route: AppRoute, definitions: RouteDefinitions, routes: (router: Router<T>) => void): [T, string, RouteParams] {
  return useMemo<[T, string, RouteParams]>(() => {
    const router = new Router<T>(route, definitions, routes);
    if (router.result == undefined || router.routeName == undefined) {
      throw new Error(`No route matched: ${route.route.join("/")}, default route handler is missing.`);
    }
    return [router.result, router.routeName, router.routeParams];
  }, [route, definitions, routes]);
}
